import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Input } from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';
import { formatMinor } from '../../../shared/utils/money';
import {
  useBookingBalanceLink,
  useCancelBooking,
  useCharterBooking,
  useConfirmBooking,
  useOverrideBookingDuration,
  useRescheduleBooking,
} from '../hooks/useCharter';
import { BookingStatusBadge, PaymentStatusBadge } from './CharterStatusBadge';
import { DetailField, InspirationPreview, IntakeResponses } from './IntakeResponses';
import { formatWat } from './charterFormat';
import type { CharterConflict } from '../types';

interface Props {
  bookingId: string;
  tenantId: string | null;
  onClose: () => void;
  /** When false, hides all action controls (TCS-admin read-only oversight). */
  canManage?: boolean;
}

export function BookingDetailModal({ bookingId, tenantId, onClose, canManage = true }: Props) {
  const { data: booking, isLoading, error } = useCharterBooking(bookingId, tenantId);
  const confirm = useConfirmBooking();
  const cancel = useCancelBooking();
  const reschedule = useRescheduleBooking();
  const override = useOverrideBookingDuration();
  const balanceLink = useBookingBalanceLink();

  const [cancelReason, setCancelReason] = useState('');
  const [newSlotId, setNewSlotId] = useState('');
  const [durationMins, setDurationMins] = useState('');
  const [conflicts, setConflicts] = useState<CharterConflict[]>([]);
  const [balanceUrl, setBalanceUrl] = useState<string | null>(null);

  useEffect(() => {
    if (booking?.durationMinutes) setDurationMins(String(booking.durationMinutes));
  }, [booking?.durationMinutes]);

  const runOverride = async () => {
    const mins = parseInt(durationMins, 10);
    if (!Number.isFinite(mins) || mins < 1) return;
    const result = await override.mutateAsync({ id: bookingId, data: { durationMinutes: mins } });
    setConflicts(result.conflicts ?? []);
  };

  const runBalanceLink = async () => {
    const result = await balanceLink.mutateAsync(bookingId);
    setBalanceUrl(result.paymentUrl);
  };

  const hasDeposit = !!booking?.deposit && (booking.remainingBalance ?? 0) > 0;

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <div className="p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Booking detail</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
            &times;
          </button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground py-8 text-center">Loading booking…</p>
        ) : error || !booking ? (
          <p className="text-red-500 py-8 text-center">Failed to load booking.</p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <DetailField label="Reference">
                <span className="font-mono text-xs">{booking.id}</span>
              </DetailField>
              <DetailField label="Status">
                <BookingStatusBadge status={booking.status} />
              </DetailField>
              <DetailField label="Payment">
                <PaymentStatusBadge status={booking.paymentStatus} />
              </DetailField>
              <DetailField label="Slot (WAT)">
                {booking.slot ? `${formatWat(booking.slot.startsAt)} → ${formatWat(booking.slot.endsAt)}` : 'Unscheduled'}
              </DetailField>
              <DetailField label="Duration">
                {booking.durationMinutes ? `${booking.durationMinutes} min` : '—'}
              </DetailField>
              <DetailField label="Deposit / balance">
                {booking.deposit ? formatMinor(booking.deposit.amount, booking.deposit.currency) : '—'}
                {booking.remainingBalance ? ` / ${formatMinor(booking.remainingBalance, booking.currency)} due` : ''}
              </DetailField>
            </div>

            {booking.customer && (
              <div className="rounded-[12px] border border-border bg-muted p-3">
                <p className="text-sm font-medium text-foreground">{booking.customer.name}</p>
                <p className="text-xs text-muted-foreground">
                  {booking.customer.email}
                  {booking.customer.phone ? ` · ${booking.customer.phone}` : ''}
                  {booking.customer.timezone ? ` · ${booking.customer.timezone}` : ''}
                </p>
              </div>
            )}

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Inspiration (referenced portfolio work)
              </p>
              <InspirationPreview inspiration={booking.inspiration} />
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Intake responses
              </p>
              <IntakeResponses formData={booking.formData} />
            </div>

            {canManage && (
              <div className="border-t border-border pt-5 space-y-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Actions</p>

                <div className="flex flex-wrap gap-3">
                  <Button
                    size="sm"
                    className="h-[38px]"
                    onClick={() => confirm.mutate(bookingId)}
                    disabled={confirm.isPending || booking.status === 'CANCELLED'}
                  >
                    Confirm
                  </Button>
                  {hasDeposit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-[38px]"
                      onClick={runBalanceLink}
                      disabled={balanceLink.isPending}
                      loading={balanceLink.isPending}
                    >
                      Send balance payment link
                    </Button>
                  )}
                </div>

                {balanceUrl && (
                  <div className="rounded-[10px] border border-border bg-muted p-3 text-sm break-all">
                    <span className="text-muted-foreground">Balance link: </span>
                    <a href={balanceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {balanceUrl}
                    </a>
                  </div>
                )}

                {/* Override this booking's duration (distinct from the service default). */}
                <div className="rounded-[12px] border border-border bg-muted p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Override duration (this booking only)</p>
                  <div className="flex items-end gap-3">
                    <Input
                      label="Duration (minutes)"
                      type="number"
                      min={1}
                      value={durationMins}
                      onChange={(e) => setDurationMins(e.target.value)}
                      className="max-w-[180px]"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-[40px]"
                      onClick={runOverride}
                      disabled={override.isPending}
                      loading={override.isPending}
                    >
                      Apply
                    </Button>
                  </div>
                  {conflicts.length > 0 && (
                    <div className="rounded-[10px] border border-amber-300 bg-amber-50 p-3">
                      <p className="text-sm font-medium text-amber-800">
                        {conflicts.length} slot/capacity conflict(s) from this change:
                      </p>
                      <ul className="mt-1 space-y-0.5 text-xs text-amber-800 list-disc list-inside">
                        {conflicts.map((c, i) => (
                          <li key={c.bookingId ?? c.slotId ?? i}>
                            {c.reason ?? 'Overlaps another booking'}
                            {c.startsAt ? ` — ${formatWat(c.startsAt)}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Reschedule to a new slot. */}
                <div className="rounded-[12px] border border-border bg-muted p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Reschedule</p>
                  <div className="flex items-end gap-3">
                    <Input
                      label="New slot id"
                      placeholder="slot_2026_07_22_1400"
                      value={newSlotId}
                      onChange={(e) => setNewSlotId(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-[40px]"
                      onClick={() => reschedule.mutate({ id: bookingId, data: { slotId: newSlotId } })}
                      disabled={!newSlotId.trim() || reschedule.isPending}
                      loading={reschedule.isPending}
                    >
                      Reschedule
                    </Button>
                  </div>
                </div>

                {/* Cancel with an optional reason. */}
                <div className="rounded-[12px] border border-border bg-muted p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Cancel booking</p>
                  <div className="flex items-end gap-3">
                    <Input
                      label="Reason (optional)"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-[40px] !text-red-600 !border-red-300"
                      onClick={() => cancel.mutate({ id: bookingId, data: { reason: cancelReason || undefined } })}
                      disabled={cancel.isPending || booking.status === 'CANCELLED'}
                      loading={cancel.isPending}
                    >
                      Cancel booking
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
